import {inject, Injectable} from "@angular/core";
import {ColorInfo} from "src/app/core/util/color-converter";
import {PerspectiveCamera, Scene} from "three";
import {LamellaSet} from "../../shared/product-card/product-card/lamel/lamella-set.class";
import {LamellaAreaObject} from "../objects-3D/lamellaAreaObject";
import {GeneralProfile} from "../../shared/product-card/product-card/profiles/general-profile/general-profile";
import {MountingProfile} from "../../shared/product-card/product-card/profiles/mounting-profile/mounting-profile";
import {ViewPortManagerStore} from "../store/view-port-manager.store";
import {CameraService} from "./camera.service";
import {RendererStore} from "../store/Renderer.store";
import {RenderingService} from "./rendering.service";


/**
 * Tieto yhdestä objektista ja sen sijoituksesta sceneen.
 */
interface ManagedObject {
    object: any;
    scene: Scene;
}

/**
 * SceneObjectManager huolehtii 3D-objektien hallinnasta ja niiden sijoittamisesta sceneihin.
 */
@Injectable({providedIn: 'root'})
export class SceneObjectManagerService {

    private static instance: SceneObjectManagerService; // Singleton-instanssi angularin ulkopuolella
    private readonly viewPortManager = inject(ViewPortManagerStore);
    private readonly cameraService = inject(CameraService);
    private readonly rendererStore = inject(RendererStore);
    private readonly renderingService = inject(RenderingService);

    constructor() {
        if (!SceneObjectManagerService.instance) {
            // Jos singleton-instanssia ei ole, luodaan se
            SceneObjectManagerService.instance = this;
        }
    }

    // Staattinen metodi singleton-instanssin hakemiseen
    public static getInstance(): SceneObjectManagerService {
        if (!SceneObjectManagerService.instance) {
            // Jos singleton-instanssia ei ole vielä olemassa, luodaan se ilman Angularia
            SceneObjectManagerService.instance = new SceneObjectManagerService();
        }
        return SceneObjectManagerService.instance;
    }

    /**
     * Välimuisti, jossa pidetään kirjaa hallituista objekteista.
     * - Avain: Objektin yksilöllinen tunniste (esim. UUID tai nimi)
     * - Arvo: ManagedObject
     */
    private objects: Map<string, ManagedObject> = new Map();

    /**
     * Lisää objektin hallintaan ja sijoittaa sen määriteltyyn sceneen.
     * @param id Objektin yksilöllinen tunniste
     * @param object Lisättävä Object3D
     * @param scene Scene, johon objekti sijoitetaan
     */
    public addObject(id: string, object: any, scene: Scene): void {
        if (this.objects.has(id)) {
            console.warn(`Object with id ${id} is already managed.`);
            return;
        }

        this.objects.set(id, {object, scene});
    }

    public getObject(id: string): ManagedObject | undefined {
        const object = this.objects.get(id);
        if (!object) {
            console.warn(`Object with id ${id} is not managed.`);
            return undefined;
        }
        return object;
    }

    public async updateLamellaAreaOrientation(id: string, orientation: 'horizontal' | 'vertical') : Promise<void> {
        const lamellaArea = this.objects.get(id).object as LamellaAreaObject;
        return lamellaArea.setOrientation(orientation)
            .then(()=>{
                this.resetCamera(id)
                this.renderingService.animateOnce();
            });
    }

    public async updateLamellaAreaSize(id: string, width : number, height : number): Promise<void> {
        const lamellaArea = this.objects.get(id).object as LamellaAreaObject;
        return lamellaArea.setSize(width, height)
            .then(()=>{
                this.resetCamera(id)
                this.renderingService.startAnimate(true)
            })
    }

    public updateMountingAndFrameProfileMaterialAndColor(id: string, mountingProfile: MountingProfile): void {
        const lamellaArea = this.objects.get(id).object as LamellaAreaObject;
        lamellaArea.updateMountingAndFrameProfileMaterialAndColor(mountingProfile);
    }

    public updateMountingAndFrameProfileColor(id: string, color: ColorInfo, mountingProfile: MountingProfile): void {
        const lamellaArea = this.objects.get(id).object as LamellaAreaObject;
        lamellaArea.data.mountingProfile = mountingProfile;
        lamellaArea.updateMountingAndFrameProfileColor(color);
        this.renderingService.animateOnce();
    }

    public removeFrameProfileFromLamellaArea(id : string): void {
        const lamellaArea = this.objects.get(id).object as LamellaAreaObject;
        lamellaArea.removeFrameProfiles();
        this.renderingService.animateOnce();
    }

    public async reBuildFrameProfilesToLamellaArea(id : string, profile : GeneralProfile, positions: {position: number, reinforced: boolean}[]): Promise<void> {
        const lamellaArea = this.objects.get(id).object as LamellaAreaObject;
        return lamellaArea.rebuildFrameProfiles(profile, positions).then(()=>{
            this.renderingService.animateOnce();
        });
    }

    public async rebuildMountingProfilesToLamellaArea(id : string, profile: GeneralProfile, positions: {position: number, reinforced: boolean}[]): Promise<void> {
        const lamellaArea = this.objects.get(id).object as LamellaAreaObject;
        return lamellaArea.rebuildMountingProfiles(profile, positions).then(()=>{
            // FIXME This need longer rendering because viewport size is effect.
            this.renderingService.startAnimate(this.viewPortManager.isCycled())
        })
    }

    public updateLamellaSetColor(lamellaAreaId: string, lamellaSetId: string, color: ColorInfo, updatedLamelSets: any): void {
        const lamellaArea = this.objects.get(lamellaAreaId).object as LamellaAreaObject
        lamellaArea.data.lamellaSets = updatedLamelSets;
        lamellaArea.setLamellaSetColor(lamellaSetId, color);
        this.renderingService.animateOnce();
    }

    public updateLamellaSetMaterialAndColor(lamellaAreaId: string, lamellaSetId: string, materialType: 'Powder Coated' | 'Anodized' | 'Galvanized Steel', color: ColorInfo): Promise<void> {
        const lamellaArea = this.objects.get(lamellaAreaId).object as LamellaAreaObject
        if (!lamellaArea) {
            return Promise.reject(new Error(`LamellaArea ${lamellaAreaId} not found`));
        }

        return lamellaArea
            .setLamellaSetMaterialAndColor(lamellaSetId, materialType, color)
            .then(() => this.renderingService.animateOnce());
    }

    public async rebuildLamellaSets(id: string, updatedLamellaSets: LamellaSet[]): Promise<void> {
        const lamellaArea = this.objects.get(id).object as LamellaAreaObject;
        return lamellaArea.rebuildSets(updatedLamellaSets)
            .then(()=> this.renderingService.animateOnce());
    }

    public resetCamera(id: string): void {
        const vp = this.viewPortManager.getById(id);
        const camera = vp.camera as PerspectiveCamera;
        const { clientWidth: width, clientHeight: height } = vp.viewPort;

        // Päivitä aspect ja projisointimatriisi
        camera.aspect = width / height;
        camera.updateProjectionMatrix();

        // Valitse FOV-kerroin eri näyttösuhteille
        const aspect = camera.aspect;
        let fovFactor: number;

        if (aspect > 1) {
            // Full screen (laajempi kuin neliö)
            fovFactor = 0.17;
        } else if (aspect === 1) {
            // Square / mobile
            fovFactor = 0.07;
        } else if (aspect < 0.35) {
            // Tablet kapea
            fovFactor = 0.5;
        } else if (aspect < 0.5) {
            // Tablet leveämpi
            fovFactor = 0.4;
        } else {
            // Desktop
            fovFactor = 0.355;
        }

        // Laske uusi target ja aseta kontrollit
        const lamellaObj = this.objects.get(id).object as LamellaAreaObject;
        const target     = this.cameraService.returnToAnchor(camera, lamellaObj, fovFactor);
        vp.controls.target.set(target.x, target.y, target.z);
    }


    public removeAllObjects(): void {
        this.objects.forEach((managedObject) => {
            const {object, scene} = managedObject;
            scene.remove(object);
        });
        this.objects.clear();
    }

    public removeObject(id: string): void {
        const managedObject = this.objects.get(id);
        if (!managedObject) {
            console.warn(`Object with id ${id} is not managed.`);
            return;
        }
        const {object, scene} = managedObject;
        scene.remove(object);
        object.destroy();
        this.objects.delete(id);

    }

}